// Default copy + timing for every step of the New Client Offer SMS journey. A new offer starts from
// these; the owner edits them in Setup (saved texts are never rewritten when a default changes). The words follow how
// guests use the offer at this salon, read when the text goes out: the `{{offer.use…}}` variables name the stand or the
// QR, and a check-in salon reads its own words through `offer-text-way-swaps.ts`, which also carries every word changed
// here over to rows saved earlier. There is no pass code to show. Reminders push online booking and never advertise
// walking in.
// No post-visit / Google review step: the salon-wide review request owns that, so an offer guest never gets
// two review asks. Copy stays GSM-7 (plain ASCII, no em dashes) so a step never silently doubles its segment
// cost.
import type { OfferMessageStep } from './new-client-offer-enums';

/** Variables an offer journey message may use (a separate set from the campaign journey's). */
export const NEW_CLIENT_OFFER_MESSAGE_VARIABLES = [
  'client.firstName',
  'salon.name',
  'offer.label',
  'offer.headline',
  'offer.passLink',
  'offer.expiresOn',
  'offer.useAction',
  'offer.useShort',
  'offer.useOnArrival',
  'booking.link',
] as const;
export type NewClientOfferMessageVariable = (typeof NEW_CLIENT_OFFER_MESSAGE_VARIABLES)[number];

/** What a step's `delayDays` counts from. IMMEDIATE steps send as soon as their trigger happens. */
export type OfferMessageTiming = 'IMMEDIATE' | 'DAYS_AFTER_SIGNUP' | 'DAYS_BEFORE_EXPIRY';

export interface OfferMessageStepMeta {
  label: string;
  /** When the step fires, in the owner's words (Setup › Messages). */
  description: string;
  timing: OfferMessageTiming;
}

export const OFFER_MESSAGE_STEP_META: Record<OfferMessageStep, OfferMessageStepMeta> = {
  PASS: { label: 'Pass', description: 'Right after sign-up', timing: 'IMMEDIATE' },
  REMINDER_1: { label: 'Reminder 1', description: 'Days after sign-up, if not booked', timing: 'DAYS_AFTER_SIGNUP' },
  REMINDER_2: { label: 'Reminder 2', description: 'Days after sign-up, if not booked', timing: 'DAYS_AFTER_SIGNUP' },
  LAST_CHANCE: { label: 'Last chance', description: 'Days before the offer expires, if not booked', timing: 'DAYS_BEFORE_EXPIRY' },
  NO_SHOW: { label: 'Missed visit', description: 'When a booked first visit is marked no-show', timing: 'IMMEDIATE' },
  WELCOME_BACK: { label: 'Welcome back', description: 'When a returning guest signs up (not eligible)', timing: 'IMMEDIATE' },
};

export interface OfferMessageDefaults {
  enabled: boolean;
  bodyText: string;
  /** Days after sign-up / before expiry (see timing); null for IMMEDIATE steps. */
  delayDays: number | null;
  /** Salon-local send hour 0-23; null for IMMEDIATE steps. */
  sendHour: number | null;
}

export const NEW_CLIENT_OFFER_DEFAULT_MESSAGES: Record<OfferMessageStep, OfferMessageDefaults> = {
  PASS: {
    enabled: true,
    bodyText:
      'Hi {{client.firstName}}! Your {{offer.label}} first visit at {{salon.name}} is ready: {{offer.passLink}} When you come in, {{offer.useAction}} to get your {{offer.label}}. Good until {{offer.expiresOn}}.',
    delayDays: null,
    sendHour: null,
  },
  REMINDER_1: {
    enabled: true,
    bodyText:
      '{{client.firstName}}, your {{offer.label}} at {{salon.name}} is waiting. Book: {{booking.link}} {{offer.useOnArrival}}',
    delayDays: 1,
    sendHour: 10,
  },
  REMINDER_2: {
    enabled: true,
    bodyText:
      '{{client.firstName}}, use your {{offer.label}} at {{salon.name}} by {{offer.expiresOn}}. Book: {{booking.link}} {{offer.useOnArrival}}',
    delayDays: 5,
    sendHour: 10,
  },
  LAST_CHANCE: {
    enabled: true,
    bodyText:
      'Last chance, {{client.firstName}}! Your {{offer.label}} ends {{offer.expiresOn}}. Book: {{booking.link}} {{offer.useOnArrival}}',
    delayDays: 3,
    sendHour: 10,
  },
  NO_SHOW: {
    enabled: true,
    bodyText:
      'We missed you today, {{client.firstName}}! Your {{offer.label}} is still good until {{offer.expiresOn}}. Pick a new time: {{booking.link}}',
    delayDays: null,
    sendHour: null,
  },
  WELCOME_BACK: {
    enabled: true,
    bodyText:
      "Hi {{client.firstName}}, thanks for thinking of {{salon.name}}! The first-visit offer is for new guests, but we'd love to see you again: {{booking.link}}",
    delayDays: null,
    sendHour: null,
  },
};
